import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { FormError, useFormError } from '@/components/ui/FormError';
import { useTranslation } from 'react-i18next';
import { idleHostSummary, idleMoney, idleText } from '@evi/shared';
import { renderInline } from '@evi/ui';
import type { TFunction } from 'i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useStreamAwareInterval } from '@/store/sessionStream';
import { ACTIVE_SESSION_KEY } from '@/hooks/useActiveSession';
import {
  ArrowLeft, Plug, Loader2, Save,
  CalendarClock, DollarSign, Users, Check, Zap, RefreshCw,
  Link2, Copy, Share2,
} from 'lucide-react';
import {
  fetchMyCharger,
  setMyChargerTimezone,
  fetchMyChargers,
  updateMyCharger,
  ackResidentDisclaimer,
  fetchConnectStatus,
  startPayoutsOrChoose,
  updateHomeGuestRate,
  startDriverCharger,
  nudgeCharger,
  OCPP_WS_BASE,
  type DriverStartError,
  type MyChargerDetail,
  type UpdateMyChargerBody,
} from '@/lib/api';
// True-margin math shared with the earn walk, so the price line here
// and the one on the walk's first step can never disagree.
import {
  computeHostMargin, hostMarginText, parseDollarsToCents, hostKeepCents,
} from '@/lib/hostMargin';
import { moneyFormat } from '@/lib/money';
import { readApiErrorEnvelope } from '@/lib/apiErrorEnvelope';
import {
  IDLE_FEE_MAX_MINUTES, IDLE_GRACE_DEFAULT_MINUTES, IDLE_GRACE_MAX_MINUTES, IDLE_GRACE_MIN_MINUTES,
  isValidIdleGraceMinutes, HOME_IDLE_RATE_MIN_CENTS, HOME_IDLE_RATE_MAX_CENTS,
} from '@evi/shared';
import { EarnPromptCard } from '@/components/home/EarnPromptCard';
import { ConnectionDetailsCard } from '@/components/home/ConnectionDetailsCard';
import { connectionViewFor, isAwaitingFirstConnection } from '@/lib/homeChargerConnection';
import { payoutChooserPath } from '@/lib/payoutCountry';
import {
  chargerGuestPricing, guestPriceBanner, heroGuestPill, openToGuestsLocked, saveCarriesMarkup,
} from '@/lib/guestPricing';
import { StatusPill } from '@/components/primitives';
import { mediaForCharger } from '@/lib/chargerMedia';
import { viewForConnectorStatus, type ConnectorStatusView } from '@/lib/connectorStatus';
import { ChargerRecoveryFlow } from './ChargerRecoveryFlow';
import { startedPlugNotice } from '@/pages/pre-session/startedPlugNotice';
import { UserTypeahead } from '@/components/ui/UserTypeahead';
import { TimeWindowRows } from '@/components/ui/TimeWindowRows';
import { DisclaimerModal } from '@/components/ui/DisclaimerModal';
import NamedIcon from '@/components/ui/NamedIcon';
import formStyles from '@/components/Form.module.css';
import { toast } from '@/store/toast';
import styles from './MyChargerDetailPage.module.css';

interface Props {
  chargerId: string;
  onNavigate: (path: string) => void;
}

interface GuestTimeWindowState {
  start_hour: number;
  end_hour: number;
  days_of_week: number[];
  /** Client-only React key. Never sent — the request body is built by
   *  picking the three wire fields explicitly (see handleSubmit), and
   *  the server schema is additionalProperties:false anyway. Rows have
   *  no server id and no naturally unique content (two windows may
   *  share hours and days), so a minted id is the only stable key. */
  _key: string;
}

/** Monotonic row key. A counter, not a random id: these never leave the
 *  tab, so uniqueness within the mounted component is the whole
 *  requirement, and a counter stays stable under React StrictMode's
 *  double-invoke where a Math.random key would not. */
let windowKeySeq = 0;
const nextWindowKey = (): string => `w${(windowKeySeq += 1)}`;

const seedWindows = (
  saved: ReadonlyArray<{ start_hour: number; end_hour: number; days_of_week: number[] }>,
): GuestTimeWindowState[] => saved.map((w) => ({
  ...w,
  days_of_week: [...w.days_of_week],
  _key: nextWindowKey(),
}));

// Day-of-week chips and hour clamping moved into the shared
// TimeWindowRows component (Phase 7.2 extraction).

/**
 * Resident-tier charger detail page. Driver-side mirror of the admin
 * ChargerDetailPage's Guest Access section, scoped to chargers the
 * caller OWNS (created_by_user_id === me; non-owners 404). The
 * settings UI is a single Save-button form; sub-tabs (Sessions,
 * Earnings, Contacts) navigate to dedicated pages.
 *
 * Component reuse:
 *   - UserTypeahead pattern: copied into apps/web/src/components/ui/
 *     UserTypeahead.tsx (driver-tier endpoint), distinct from the
 *     admin one which is admin-gated. Follow-up: extract to shared
 *     once the admin/PWA UI primitives stabilize.
 *   - Time-windows table: inline copy of the TariffForm TOU rows
 *     pattern. Phase 7's electricity tariff editor extracts a
 *     shared component.
 */
export function MyChargerDetailPage({ chargerId, onNavigate }: Props) {
  const { t } = useTranslation('myChargers');
  const qc = useQueryClient();
  const { data: charger, isLoading, error } = useQuery({
    queryKey: ['my-charger', chargerId],
    queryFn: () => fetchMyCharger(chargerId),
  });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('detail.back')}
          onClick={() => onNavigate('/my-chargers')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>
            {charger?.identity ?? (isLoading ? t('detail.loading') : t('detail.charger'))}
          </h1>
          {charger && (
            <p className={styles.subtitle}>{charger.location_name}</p>
          )}
        </div>
      </header>

      {isLoading && (
        <div className={styles.skeleton} />
      )}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('detail.error.load')}
        </div>
      )}

      {charger && (
        <DetailBody
          charger={charger}
          chargerId={chargerId}
          onNavigate={onNavigate}
          onSaved={(next) => {
            // PATCH /my-chargers/:id answers without home_monetization (the
            // GET builds it). Writing that answer over the cache dropped the
            // home pricing block, idle fee and grace included, until a refetch,
            // so the host saw what they had just saved disappear. With no
            // pricing in the answer, refetch instead: the form keeps what was
            // typed until the saved values arrive.
            if (next.home_monetization === undefined && charger.home_monetization !== undefined) {
              void qc.invalidateQueries({ queryKey: ['my-charger', chargerId] });
            } else {
              qc.setQueryData(['my-charger', chargerId], next);
            }
            qc.invalidateQueries({ queryKey: ['my-chargers'] });
          }}
        />
      )}
    </div>
  );
}

interface DetailBodyProps {
  charger: MyChargerDetail;
  chargerId: string;
  onNavigate: (path: string) => void;
  onSaved: (next: MyChargerDetail) => void;
}

function DetailBody({ charger, chargerId, onNavigate, onSaved }: DetailBodyProps) {
  const { t, i18n } = useTranslation('myChargers');
  // The earn walk's "add your home's country" line, shown for the same
  // refusal here (HOME_COUNTRY_REQUIRED).
  const { t: tEarn } = useTranslation('earn');
  // Local form state — initialized from server, re-initialized on
  // any external charger update (background refetch, post-save).
  // Ref-locked initialization avoids re-running setState on every
  // unrelated render.
  const [isOpen, setIsOpen] = useState(charger.is_open_to_guests);
  const [publicNetwork, setPublicNetwork] = useState(charger.public_network);
  // Markup edited as a percentage in the input (0–500), persisted as
  // basis points (0–50000). Empty string → null = "not guest-priced"
  // (which the canGuestUseCharger gate treats as functionally closed).
  const [markupPctStr, setMarkupPctStr] = useState(
    typeof charger.guest_markup_bps === 'number'
      ? (charger.guest_markup_bps / 100).toString()
      : '',
  );
  const [passList, setPassList] = useState<string[]>(charger.guest_pass_list);
  const [denyList, setDenyList] = useState<string[]>(charger.guest_deny_list ?? []);
  // LAZY initializer. Eager (`useState(seedWindows(...))`) re-runs
  // seedWindows on every single render and throws the result away,
  // minting a fresh _key per window per render and walking the counter
  // with render count. Harmless to correctness, but it makes the key
  // sequence meaningless and burns work on a hot path.
  const [windows, setWindows] = useState<GuestTimeWindowState[]>(
    () => seedWindows(charger.guest_time_windows),
  );

  // ?scroll=pass-list — the Contacts empty state deep-links here so
  // "add someone" lands on the Free pass list rather than the top of
  // a long settings form. Mount-only: DetailBody exists only once the
  // charger has loaded, so the section is already in the DOM.
  // ?scroll=guest-price / ?scroll=guest-access: the "a guest couldn't
  // charge" notification (item 6) lands on its banner, or, once the banner
  // is gone, on the price field at a home and on the Open to guests switch
  // at a building.
  const passListSectionRef = useRef<HTMLDivElement>(null);
  const guestBannerRef = useRef<HTMLDivElement>(null);
  const openToGuestsRowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const target = params.get('scroll');
    if (target === 'pass-list') {
      passListSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (target === 'guest-price') {
      (guestBannerRef.current ?? document.getElementById('guest-rate-input'))
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (target === 'guest-access') {
      (guestBannerRef.current ?? openToGuestsRowRef.current)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);
  const scrollToPassList = (): void => {
    passListSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  // The building banner's main action: the switch that stops guests being
  // turned away. Scrolls to it and focuses it; the resident turns it off
  // and saves, as with any other setting here.
  const goToOpenToGuests = (): void => {
    const row = openToGuestsRowRef.current;
    row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    row?.querySelector<HTMLButtonElement>('button[role="switch"]')?.focus({ preventScroll: true });
  };

  // Slice D — residential branch. home is null at multifamily chargers,
  // so every isHome branch below leaves those surfaces exactly as today.
  const home = charger.home_monetization;
  const isHome = home != null;
  // Item 6 (Warren, 2026-10-02: "No paid guests yet" at building plugs). A
  // resident's own charger anywhere but a home has no guest price a guest
  // can pay, so its markup editor is retired (the stored guest_markup_bps
  // is left alone, never sent) and a closed one cannot be opened to
  // guests. Chargers the resident did not register keep the markup.
  const isBuildingResident = !isHome && chargerGuestPricing(charger) === 'building_unavailable';
  const openLocked = isBuildingResident && openToGuestsLocked(charger);
  // Open to guests with no guest price: every paying guest is turned away.
  const priceBanner = guestPriceBanner(charger);
  // The hero says so too, instead of a healthy "Open to guests".
  const guestPill = heroGuestPill(charger);
  // Every home amount on this page is in the home's currency (item 3):
  // CA$ at a Canadian home. A payload without the field reads USD. That
  // is the currency a save sets the price in, so the input prefix, the
  // margin line and the idle-fee lines all use it.
  const m = moneyFormat(home?.currency, i18n.language);
  // A home priced before item 3 (a Canadian home with a USD price): guests
  // pay the stored price in guest_rate_currency until a save moves it to
  // the home's currency. The card says so, and its Save sends the price
  // and the idle fee as shown, so the move stores exactly what was on
  // screen.
  const storedPriceCurrency = home?.guest_rate_currency ? home.guest_rate_currency.toUpperCase() : null;
  const currencyMove = isHome && home.currency != null
    && storedPriceCurrency !== null && storedPriceCurrency !== m.currency;
  // A symbol wider than $ (CA$) needs the input text to start later.
  const homeInputClass = m.symbol.length > 1
    ? `${styles.pricingInput} ${styles.pricingInputWide}`
    : styles.pricingInput;
  // The home guest price, edited in dollars, persisted in cents via its
  // own PATCH /me/home/guest-rate on Save.
  const [guestRateStr, setGuestRateStr] = useState(
    home?.guest_rate_cents != null ? (home.guest_rate_cents / 100).toFixed(2) : '',
  );
  // The idle fee (per idle minute), edited in dollars, persisted in
  // cents on the same Home Guest tariff as the rate. Blank means "no
  // fee" rather than "leave alone" — the fee is optional, so blank is
  // the only way to express removal, and clearing it can never be
  // invalid.
  //
  // There is no host fee field beside it any more (Tier 2): the
  // connection fee is EVI's, set per charger by EVI and kept by EVI, so
  // this page only shows it (home.connection_fee_cents, read-only).
  const connectionFeeCents = home?.connection_fee_cents ?? null;
  const [idleFeeStr, setIdleFeeStr] = useState(
    home?.time_rate_cents != null ? (home.time_rate_cents / 100).toFixed(2) : '',
  );
  // The idle fee's grace (Warren, 2026-09-28: required with every idle fee,
  // 5 to 30 minutes, default 15). Prefilled with the default so a host who
  // types a fee has a valid grace unless they clear it.
  const [idleGraceStr, setIdleGraceStr] = useState(
    String(home?.idle_grace_minutes ?? IDLE_GRACE_DEFAULT_MINUTES),
  );
  const [idleLimitStr, setIdleLimitStr] = useState(home?.idle_max_charge_cents != null ? String(home.idle_max_charge_cents / 100) : '');

  // ── True-margin math (home branch) ─────────────────────────────────
  // The host receives 85% of the guest price — the settlement is a
  // destination charge to the home tenant's Connect account with EVI's
  // 15% platform fee carved out (EVI's connection fee rides on top and
  // is EVI's whole). So the honest margin is
  //   0.85 x guest price − electricity cost estimate
  // never guest price − cost, which would overstate by the fee. The
  // arithmetic lives in lib/hostMargin (shared with the earn walk and
  // the entry-point cards); this page only feeds it the typed price.
  // Recomputed from guestRateStr LIVE so the line answers "is this
  // price worth it" while the owner is still typing it.
  const utilityRate = home?.utility_rate ?? null;
  const liveGuestCents = parseDollarsToCents(guestRateStr);

  // ── Idle-fee parsing ───────────────────────────────────────────────
  // Dollars in the field, cents on the wire. Comma decimals are
  // normalised the same way the rate field does it, so a localised
  // numeric keypad cannot produce a NaN that reads as garbage.
  const typedIdleCents = parseDollarsToCents(idleFeeStr);
  // What the host actually banks on the idle fee. It is NOT exempt from
  // EVI's 15% — it settles through the same destination charge as the
  // energy — so quoting the gross fee here would overstate their take
  // by exactly the carve-out.
  const idleKeepCents = typedIdleCents != null ? hostKeepCents(typedIdleCents) : null;
  // Idle bills for at most IDLE_FEE_MAX_MINUTES a session (Warren,
  // 2026-09-27), so the most a guest can pay in idle is rate x that.
  const idleMaxCents = typedIdleCents != null && typedIdleCents > 0
    ? Math.min(typedIdleCents * IDLE_FEE_MAX_MINUTES, parseDollarsToCents(idleLimitStr) ?? Infinity)
    : null;
  const hasTypedIdleFee = typedIdleCents != null && typedIdleCents > 0;
  const typedGrace = idleGraceStr.trim() === '' ? null : Number(idleGraceStr);
  const utilityDisplay = (() => {
    if (!utilityRate) return null;
    if (utilityRate.type === 'flat') {
      return utilityRate.base_cents != null ? `${m.money(utilityRate.base_cents)} /kWh` : null;
    }
    if (utilityRate.min_cents == null || utilityRate.max_cents == null) return null;
    const range = utilityRate.min_cents === utilityRate.max_cents
      ? m.money(utilityRate.min_cents)
      : `${m.money(utilityRate.min_cents)}-${m.money(utilityRate.max_cents)}`;
    return `${range} /kWh ${utilityRate.type === 'time_of_use'
      ? t('detail.utilityRate.tagTou')
      : t('detail.utilityRate.tagTiered')}`;
  })();
  // Null until a positive price is typed; then flat / range / noRate per
  // the owner's estimate, worded by the same detail.utilityRate.* keys.
  const liveMargin = computeHostMargin(liveGuestCents, utilityRate);
  const marginLine = liveMargin
    ? hostMarginText(liveMargin, t, connectionFeeCents, { currency: m.currency, locale: m.locale })
    : null;

  const [saving, setSaving] = useState(false);
  // useFormError, not useState: this handler clears then re-sets in the
  // same tick (setErrorMsg('') at the top, a guard further down, no
  // await between), so React batched an identical repeat into no state
  // change at all — first failure loud, every repeat after it silent.
  // The nonce makes each occurrence distinct. See FormError.tsx.
  //
  // The page-level errorRef + scrollIntoView effect that used to sit
  // here was dead: the ref was never attached to anything once the
  // markup moved to <FormError>, which owns the scroll itself.
  const [errorMsg, setErrorMsg] = useFormError();
  // Holds the pending PATCH body when the server returns
  // 412 DISCLAIMER_REQUIRED so the disclaimer modal can retry the
  // exact same body on ack. Cleared on cancel or after successful
  // retry. Null when no disclaimer prompt is active.
  const [pendingPatch, setPendingPatch] = useState<UpdateMyChargerBody | null>(null);
  // Set when the server returns 402 EXPRESS_ONBOARDING_REQUIRED.
  // Surfaces a "Set up payouts" CTA inline; tapping kicks off the
  // /me/connect/start flow which redirects to Stripe-hosted
  // onboarding. After return, the resident comes back here and
  // tries Save again. With no account yet the CTA goes to the payout
  // chooser instead, which asks for the bank account country (item 2):
  // this page cannot, and the server creates no account without one.
  const [needsExpressOnboarding, setNeedsExpressOnboarding] = useState(false);
  const connectQc = useQueryClient();

  // Re-sync local state when the underlying charger doc changes (e.g.
  // background refetch surfaced a remote update). Doesn't fire on
  // every keystroke — only when the prop reference changes, which
  // only happens through React-Query.
  // Re-seed the form ONLY when the SAVED server state actually
  // changes — never on object identity alone. React-Query refetches
  // on window focus and returns a fresh object even when nothing
  // changed, and depending on [charger] made every app switch (take
  // a screenshot, check a text) silently stomp staged edits back to
  // the saved values; the next Save then faithfully persisted the
  // stomped state ("saved but lost my change" — Warren hit this with
  // a day chip 2026-08-09). savedSig serializes every seeded field,
  // so it is the whole dependency set — same pattern as
  // HomeSettingsPage. A genuine remote change mid-edit still
  // re-seeds, which is the pre-existing (and intended) behavior.
  const savedSig = JSON.stringify({
    o: charger.is_open_to_guests,
    p: charger.public_network,
    g: charger.home_monetization?.guest_rate_cents ?? null,
    tr: charger.home_monetization?.time_rate_cents ?? null,
    ig: charger.home_monetization?.idle_grace_minutes ?? null,
    m: charger.guest_markup_bps ?? null,
    pl: charger.guest_pass_list,
    dl: charger.guest_deny_list ?? [],
    w: charger.guest_time_windows,
  });
  // Skip the FIRST run. Every state above is already seeded from the
  // same `charger` by its useState initializer, so the mount pass
  // re-set identical values — harmless for the scalars, but
  // seedWindows() mints a SECOND set of _keys, which tears down and
  // rebuilds every window row one frame after mount. Under the old
  // index keys that was invisible; with stable keys it destroys any
  // focus or in-flight interaction inside a row. Only a genuine
  // post-mount change to saved server state should re-seed.
  const appliedSigRef = useRef(savedSig);
  useEffect(() => {
    if (appliedSigRef.current === savedSig) return;
    appliedSigRef.current = savedSig;
    setIsOpen(charger.is_open_to_guests);
    setPublicNetwork(charger.public_network);
    setGuestRateStr(
      charger.home_monetization?.guest_rate_cents != null
        ? (charger.home_monetization.guest_rate_cents / 100).toFixed(2)
        : '',
    );
    setMarkupPctStr(
      typeof charger.guest_markup_bps === 'number'
        ? (charger.guest_markup_bps / 100).toString()
        : '',
    );
    const hm = charger.home_monetization;
    setIdleFeeStr(hm?.time_rate_cents != null ? (hm.time_rate_cents / 100).toFixed(2) : '');
    setIdleLimitStr(hm?.idle_max_charge_cents != null ? String(hm.idle_max_charge_cents / 100) : '');
    setIdleGraceStr(String(hm?.idle_grace_minutes ?? IDLE_GRACE_DEFAULT_MINUTES));
    setPassList(charger.guest_pass_list);
    setDenyList(charger.guest_deny_list ?? []);
    setWindows(seedWindows(charger.guest_time_windows));
    // savedSig serializes every seeded field — the whole dependency set.
  }, [savedSig]);

  /** Markup percent string → bps for the wire. Empty/NaN/negative → null.
   *  Caps at 50000 bps (500%) at the form layer; server schema
   *  enforces the same cap. */
  const markupBpsForSave = useMemo<number | null>(() => {
    const trimmed = markupPctStr.trim();
    if (!trimmed) return null;
    const pct = Number(trimmed);
    if (!Number.isFinite(pct) || pct < 0) return null;
    return Math.min(50_000, Math.round(pct * 100));
  }, [markupPctStr]);

  // ── B.1 guest-pricing dollar derivation ────────────────────────────
  // Reference rate the $ ↔ % conversion uses. Flat → the flat rate;
  // TOU sources → the average of min/max (the UI surfaces the range
  // separately so the resident knows the $ is an approximation);
  // none/zero → null, which disables the $ input + math display.
  // Reads off the resolved_rate snapshot the server hydrates onto
  // the detail response.
  const loc = charger.location;
  const resolved = loc?.resolved_rate ?? null;
  const buildingRefRateCents = useMemo<number | null>(() => {
    if (!resolved) return null;
    if (resolved.source === 'flat') return resolved.rate_cents;
    if (resolved.source === 'inline_tou' || resolved.source === 'tariff') {
      if (resolved.tou_min_cents != null && resolved.tou_max_cents != null) {
        return Math.round((resolved.tou_min_cents + resolved.tou_max_cents) / 2);
      }
      return resolved.rate_cents;
    }
    return null;
  }, [resolved]);

  // Local dollar-input state. Initialized from bps × ref-rate; kept
  // in sync by editing the % side. The two states are independent on
  // purpose — typing in $ shouldn't echo back through bps and
  // re-format the input while the resident is mid-keystroke.
  const initialDollarStr = useMemo<string>(() => {
    if (buildingRefRateCents == null) return '';
    if (typeof charger.guest_markup_bps !== 'number') return '';
    const dollars = (charger.guest_markup_bps / 10_000) * (buildingRefRateCents / 100);
    return dollars > 0 ? dollars.toFixed(4) : '';
    // The initial seed only — once the user starts editing, useEffect
    // below re-seeds on prop changes (background refetch / save).
  }, [charger.guest_markup_bps, buildingRefRateCents]);
  const [markupDollarStr, setMarkupDollarStr] = useState(initialDollarStr);
  useEffect(() => {
    setMarkupDollarStr(initialDollarStr);
  }, [initialDollarStr]);

  // % → $ sync. Fires when the resident edits the % side. Computes
  // the derived dollar value and writes it into the $ input. No-op
  // when there's no reference rate (TOU min/max unknown, or none/zero
  // building rate) — the $ input is disabled in those cases anyway.
  const handlePctChange = (next: string): void => {
    setMarkupPctStr(next);
    if (buildingRefRateCents == null || buildingRefRateCents === 0) return;
    const pct = Number(next);
    if (!Number.isFinite(pct) || pct < 0) {
      setMarkupDollarStr('');
      return;
    }
    const dollars = (pct / 100) * (buildingRefRateCents / 100);
    setMarkupDollarStr(dollars > 0 ? dollars.toFixed(4) : '');
  };

  // $ → % sync. Fires when the resident edits the $ side. Computes
  // bps via `bps = round($ × 10000 / building_rate_dollars)` and
  // surfaces it back through the % string (which is still the source
  // of truth for the wire).
  const handleDollarChange = (next: string): void => {
    setMarkupDollarStr(next);
    if (buildingRefRateCents == null || buildingRefRateCents === 0) return;
    const dollars = Number(next);
    if (!Number.isFinite(dollars) || dollars < 0) {
      setMarkupPctStr('');
      return;
    }
    const buildingDollars = buildingRefRateCents / 100;
    const pct = (dollars / buildingDollars) * 100;
    // Clamp to the existing 0–500% form-layer cap. Same cap server-
    // side; if the resident exceeds it via the $ input, the % side
    // displays the clamp.
    const clamped = Math.min(500, pct);
    setMarkupPctStr(clamped > 0 ? clamped.toFixed(1) : '');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrorMsg('');

    // An empty row is DROPPED, not rejected. Until 2026-08-09 a window
    // with no days selected aborted the entire save — before any
    // network call — and the error rendered ~270 lines above the Save
    // button, off-screen on a phone. A host who tapped "Add window"
    // and changed their mind was silently locked out of saving
    // anything, permanently, with the button appearing to work. That
    // cost Warren six test cycles.
    //
    // Dropping is also the semantically correct answer: a no-day
    // window matches nothing on the canGuest gate, so storing it and
    // discarding it are indistinguishable to every consumer. It
    // expresses no intent, so it cannot be wrong to discard.
    const windowsForSave = windows.filter((w) => w.days_of_week.length > 0);

    setSaving(true);
    const body: UpdateMyChargerBody = {
      is_open_to_guests: isOpen,
      public_network: publicNetwork,
      // A HOME charger has no markup — its guest price is an absolute
      // rate on the Home Guest tariff — so the field is OMITTED here,
      // not sent as null. The server's `has()` is presence-based
      // (hasOwnProperty && !== undefined), so sending the key at all
      // puts this charger through a residential guard it can only
      // fail: markupPctStr has five writers, one of them the dollar-to-
      // percent converter, and any of them turning the field non-empty
      // makes markupBpsForSave a number. Stored is null, so that reads
      // as a changed value and the guard 400s with "Guest pricing for a
      // home charger is its guest rate, not a markup" — blocking a
      // window edit for a field the host never touched.
      //
      // The server guard is CORRECT and stays. A residential charger
      // must never carry a markup. The bug was the client offering one.
      // A building resident's charger omits it too (item 6): its markup
      // editor is retired and the stored value is left as it is.
      ...(!isHome && saveCarriesMarkup(charger) ? { guest_markup_bps: markupBpsForSave } : {}),
      guest_pass_list: passList,
      guest_deny_list: denyList,
      guest_time_windows: windowsForSave.map((w) => ({
        start_hour: w.start_hour,
        end_hour: w.end_hour,
        days_of_week: [...w.days_of_week].sort((a, b) => a - b),
      })),
    };
    try {
      // Home guest price rides its own endpoint (the price lives on the
      // Home Guest tariff, not the charger). Saved first so a rejected
      // rate never half-applies alongside the charger PATCH.
      if (isHome && home.enrolled) {
        // Validate the EFFECTIVE price, not the input field.
        //
        // This used to read a blank or unparseable field as "this
        // charger has no price" and abort the whole save — including
        // the guest windows, which have nothing to do with pricing —
        // even though a perfectly good rate was already stored and
        // live. Any path leaving the field empty at save time (first
        // paint before the detail query re-seeds it, a comma decimal
        // from a localised numeric keypad parsing to NaN, a cleared
        // field) locked the host out of saving anything, and the error
        // it showed named a price range that was not actually wrong.
        //
        // A blank field now means "leave the price alone", which is
        // what a blank field has always meant everywhere else in this
        // app. Only text that is present AND unparseable is an error,
        // and only a charger with no stored price at all can block
        // opening to guests.
        const trimmedRate = guestRateStr.trim().replace(',', '.');
        const typedCents = trimmedRate === ''
          ? null
          : Math.round(Number.parseFloat(trimmedRate) * 100);
        const storedCents = home.guest_rate_cents ?? null;
        // What the guest would actually be charged after this save.
        const effectiveCents = typedCents ?? storedCents;
        const typedIsGarbage = trimmedRate !== ''
          && (typedCents == null || !Number.isFinite(typedCents));
        if (typedIsGarbage
          || (isOpen && (effectiveCents == null
            || effectiveCents < 1 || effectiveCents > 200))) {
          setErrorMsg(t('detail.validation.guestPriceRange', { min: m.money(1), max: m.money(200) }));
          setSaving(false);
          return;
        }
        // Only a CHANGED fee is sent, per
        // docs/tickets/per-field-validation-scope.md. A host editing
        // their guest windows must never be blocked by a fee they did
        // not touch.
        const idleChanged = typedIdleCents !== (home.time_rate_cents ?? null);
        const maximum = idleLimitStr.trim() === '' ? null : Math.round(Number(idleLimitStr) * 100);
        if (maximum != null && (!Number.isSafeInteger(maximum) || maximum < 0)) {
          setErrorMsg(t('detail.validation.idleMaximum')); setSaving(false); return;
        }
        const maximumChanged = maximum !== (home.idle_max_charge_cents ?? null);
        // A currency move sends the idle fee as shown, changed or not.
        const idleSend = idleChanged || currencyMove;
        if (idleSend && hasTypedIdleFee && (typedIdleCents == null || typedIdleCents < HOME_IDLE_RATE_MIN_CENTS || typedIdleCents > HOME_IDLE_RATE_MAX_CENTS)) {
          setErrorMsg(t('detail.validation.idleFeeRange', { min: m.money(HOME_IDLE_RATE_MIN_CENTS), max: m.money(HOME_IDLE_RATE_MAX_CENTS) }));
          setSaving(false);
          return;
        }
        const graceChanged = hasTypedIdleFee
          && typedGrace !== (home.idle_grace_minutes ?? IDLE_GRACE_DEFAULT_MINUTES);
        // An idle fee without a grace is refused (Warren, 2026-09-28), but
        // only when the fee or its grace is being changed: an untouched fee
        // never blocks another edit.
        if ((idleSend || graceChanged) && hasTypedIdleFee && !isValidIdleGraceMinutes(typedGrace)) {
          setErrorMsg(t('detail.validation.idleGraceRange', {
            min: IDLE_GRACE_MIN_MINUTES, max: IDLE_GRACE_MAX_MINUTES,
          }));
          setSaving(false);
          return;
        }

        const cents = effectiveCents;
        const rateChanged = cents != null && Number.isFinite(cents)
          && cents >= 1 && cents <= 200
          && cents !== (home.guest_rate_cents ?? null);
        // One PATCH carries whichever legs moved. The idle-fee key is
        // omitted entirely when unchanged, so an untouched fee can
        // neither be cleared by accident nor re-validated. No connection
        // fee is ever sent: it is EVI's. A currency move (item 3) is a
        // change of every leg: the save the card's note asks for.
        if (rateChanged || idleChanged || graceChanged || maximumChanged || currencyMove) {
          // The price is set in the home's currency, which a home with no
          // country does not have: the server refuses it (409
          // HOME_COUNTRY_REQUIRED), so say it here first, in the host's
          // language. A save that leaves the price alone is not blocked.
          if (home.currency === null) {
            setErrorMsg(tEarn('price.validation.countryRequired'));
            setSaving(false);
            return;
          }
          const rateForWrite = cents != null && Number.isFinite(cents)
            && cents >= 1 && cents <= 200
            ? cents
            : home.guest_rate_cents;
          // A fee edit with no stored rate has nothing to price
          // against, and the endpoint requires a rate, so bail rather
          // than inventing one.
          if (rateForWrite != null) {
            await updateHomeGuestRate(rateForWrite, {
              ...(maximumChanged ? { idleMaxChargeCents: maximum } : {}),
              ...(idleSend ? { timeRateCents: typedIdleCents } : {}),
              ...((idleSend || graceChanged) && hasTypedIdleFee && typedGrace != null
                ? { idleGraceMinutes: typedGrace }
                : {}),
            });
          }
        }
      }
      const updated = await updateMyCharger(chargerId, body);
      onSaved(updated);
      toast(t('detail.toastSaved'), { tone: 'accent' });
    } catch (err) {
      const { code, message: serverMsg } = await readApiErrorEnvelope(err);
      // 412 DISCLAIMER_REQUIRED — first pass-list contact ever.
      if (code === 'DISCLAIMER_REQUIRED') {
        setPendingPatch(body);
        setSaving(false);
        return;
      }
      // 402 OPERATOR_NOT_CONFIGURED — building's tenant Connect
      // isn't ready. Surface as inline copy with no action button:
      // the resident can't fix this from their side, the operator
      // must finish onboarding.
      if (code === 'OPERATOR_NOT_CONFIGURED') {
        setErrorMsg(isHome
          ? t('detail.error.enrollToOpen')
          : t('detail.error.buildingNotConfigured'));
        return;
      }
      // 402 EXPRESS_ONBOARDING_REQUIRED — resident's Express isn't
      // ready. The dedicated "Set up payouts" button below opens
      // /my-account/connect/start (the page that mints the link).
      if (code === 'EXPRESS_ONBOARDING_REQUIRED') {
        setNeedsExpressOnboarding(true);
        return;
      }
      // 409 HOME_COUNTRY_REQUIRED (item 3): no country, so no currency to
      // set the price in. The server's message is English-only.
      if (code === 'HOME_COUNTRY_REQUIRED') {
        setErrorMsg(tEarn('price.validation.countryRequired'));
        return;
      }
      // 409 BUILDING_GUEST_PRICING_UNAVAILABLE (item 6): opening a building
      // resident's charger to guests. The server's message is English-only.
      if (code === 'BUILDING_GUEST_PRICING_UNAVAILABLE') {
        setIsOpen(charger.is_open_to_guests);
        setErrorMsg(t('detail.error.buildingGuestPricing'));
        return;
      }
      // Unknown code: the server's own message is the explanation the
      // user needs (e.g. the residential markup guard's "update the
      // app" copy). ky's HTTPError message is a status line, useless
      // to a person — prefer the localized generic over it, and keep
      // err.message only for genuine non-HTTP failures.
      const isHttpError = err instanceof Error && err.name === 'HTTPError';
      setErrorMsg(
        serverMsg
        ?? (isHttpError ? t('detail.error.save') : err instanceof Error ? err.message : t('detail.error.save')),
      );
    } finally {
      setSaving(false);
    }
  };

  // Disclaimer ack handler — wired into DisclaimerModal. Acks
  // server-side, then replays the queued PATCH body. Errors during
  // either step surface in the modal's inline error region; the
  // modal stays open until the resident either retries or cancels.
  const handleAckDisclaimer = async (): Promise<void> => {
    await ackResidentDisclaimer();
    if (!pendingPatch) {
      return;
    }
    const updated = await updateMyCharger(chargerId, pendingPatch);
    onSaved(updated);
    setPendingPatch(null);
    toast(t('detail.toastSaved'), { tone: 'accent' });
  };

  const connector = charger.connectors[0];

  // Live connector status: the shared my-chargers LIST query (already
  // 10s-foreground-polled by My Home and the list page; same cache key)
  // rather than polling the detail doc — a detail refetch re-syncs the
  // whole settings form (the effect above) and would stomp mid-edit
  // state every time the connector status flips during a session.
  // Live charger events refetch it; 10s is the fallback poll.
  const liveRefreshMs = useStreamAwareInterval(10_000);
  const liveQ = useQuery({
    queryKey: ['my-chargers'],
    queryFn: fetchMyChargers,
    refetchInterval: liveRefreshMs,
    refetchIntervalInBackground: false,
  });
  const live = liveQ.data?.find((c) => c._id === chargerId);
  // Same canonical mapping as the public station rows and the My Home
  // card: Offline wins, else the live connector state.
  const statusView = viewForConnectorStatus(
    (live ?? charger).connectors[0]?.status,
    (live ?? charger).is_online,
  );
  // Register-first: a charger that has never connected is waiting for its
  // first connection, not offline. It gets the setup card instead of the
  // offline nudge and recovery flow, which have nothing to recover yet.
  const { t: tHome } = useTranslation('myHome');
  const awaitingFirst = isAwaitingFirstConnection(live ?? charger);
  const connectionView = (live ?? charger).connection
    ? connectionViewFor(live ?? charger, OCPP_WS_BASE)
    : null;
  const refreshConnection = () => {
    void liveQ.refetch();
  };
  // Model-matched ambient loop for the hero when the owner has not set
  // a photo. Unmatched models keep the plug glyph exactly as before.
  const media = mediaForCharger(charger);
  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────── */}
      <div className={styles.hero}>
        {charger.photo_url ? (
          <img src={charger.photo_url} alt="" className={styles.heroImg} loading="lazy" />
        ) : media ? (
          reduceMotion ? (
            <img src={media.poster} alt="" className={styles.heroImg} />
          ) : (
            <video
              className={styles.heroImg}
              src={media.loop}
              poster={media.poster}
              autoPlay
              muted
              loop
              playsInline
            />
          )
        ) : (
          <div className={styles.heroFallback} aria-hidden>
            <Plug size={120} strokeWidth={1.25} />
          </div>
        )}
        <div className={styles.heroPills}>
          <StatusPill tone={awaitingFirst ? 'info' : statusView.tone} variant="solid" size="sm">
            {awaitingFirst ? tHome('connection.waitingPill') : statusView.label}
          </StatusPill>
          {guestPill === 'open' && (
            <StatusPill tone="accent" variant="solid" size="sm">
              {t('detail.openToGuestsPill')}
            </StatusPill>
          )}
          {guestPill === 'turned_away' && (
            <StatusPill tone="warn" variant="solid" size="sm">
              {t('detail.guestsTurnedAwayPill')}
            </StatusPill>
          )}
        </div>
      </div>

      {/* Owner remote start — every charger the driver owns, home or
          building. Same driver-start helper as the My Home button, and
          the same one the pre-session page uses at a building, so the
          detail page covers the action an owner reaches for most
          without a trip back. Whether the start is free is decided
          server-side (the residential owner path), not by this gate. */}
      {awaitingFirst && connectionView ? (
        <div className={styles.startSection}>
          <p className={styles.startNote}>{t('detail.owner.setupNote')}</p>
          <ConnectionDetailsCard
            chargerId={chargerId}
            connection={connectionView}
            variant="setup"
            onChanged={refreshConnection}
          />
        </div>
      ) : (
        <OwnerStartSection
          charger={charger}
          statusView={statusView}
          isOnline={(live ?? charger).is_online}
          onNavigate={onNavigate}
        />
      )}

      {/* ── Sub-tab navigation ─────────────────────────────────────
          Earnings / Sessions / Contacts — Earnings leads because it's
          the higher-frequency interest for a resident-managed charger
          owner. The page's .page container uses gap:16px between
          top-level children; the .subnav block carries no own margins,
          so the order in JSX is the order on screen with no CSS
          retuning. */}
      <nav className={styles.subnav} aria-label={t('detail.subnav.aria')}>
        <button
          type="button"
          className={styles.subnavItem}
          onClick={() => onNavigate(`/my-chargers/${chargerId}/earnings`)}
        >
          {/* MEDIUM 6.2 — was 'earnings'; identical to wallet.json.
              Icon scaled 16 → 40 in the polish pass so the row reads
              as an obvious tap affordance instead of a list-item.
              The Lottie NamedIcon also gets scaled. */}
          <NamedIcon name="wallet" fallback={<DollarSign size={40} />} size={40} />
          <span>
            <span className={styles.subnavLabel}>{t('detail.subnav.earningsLabel')}</span>
            <span className={styles.subnavHint}>{t('detail.subnav.earningsHint')}</span>
          </span>
        </button>
        <button
          type="button"
          className={styles.subnavItem}
          onClick={() => onNavigate(`/my-chargers/${chargerId}/sessions`)}
        >
          {/* calendar.json was re-wrapped onto a square tight 384x384 canvas
              (was 650x320 landscape with ~50% side padding, which letterboxed
              in any square box and forced the 72px compensation). At 52 the
              visible mark now matches the Contacts row and fits the 52px
              icon column exactly. */}
          <NamedIcon name="calendar" fallback={<CalendarClock size={40} />} size={52} />
          <span>
            <span className={styles.subnavLabel}>{t('detail.subnav.sessionsLabel')}</span>
            <span className={styles.subnavHint}>{t('detail.subnav.sessionsHint')}</span>
          </span>
        </button>
        <button
          type="button"
          className={styles.subnavItem}
          onClick={() => onNavigate(`/my-chargers/${chargerId}/contacts`)}
        >
          {/* Warren's contactsbook.json replaces the placeholder 'contacts'
              slot; bumped 40 -> 52 (Warren pass 2) so the row reads at the
              same optical weight as the wallet. */}
          <NamedIcon name="contactsbook" fallback={<Users size={40} />} size={52} />
          <span>
            <span className={styles.subnavLabel}>{t('detail.subnav.contactsLabel')}</span>
            <span className={styles.subnavHint}>{t('detail.subnav.contactsHint')}</span>
          </span>
        </button>
      </nav>

      {/* ── Connector specs (read-only) ─────────────────────────── */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>{t('detail.connector.title')}</h2>
        {charger.connectors.length === 0 ? (
          <p className={styles.cardEmpty}>{t('detail.connector.empty')}</p>
        ) : (
          <ul className={styles.specList}>
            {charger.connectors.map((c) => {
              // Live, canonical status per connector — the same 10s-polled doc
              // and viewForConnectorStatus mapping the hero pills use (Offline
              // wins, tone per state: Available=good green, Charging=info,
              // Faulted=danger, ...), instead of the previous static mute pill.
              const liveStatus = (live ?? charger).connectors.find(
                (lc) => lc.connector_id === c.connector_id,
              )?.status ?? c.status;
              // Never connected: the same "Waiting for first connection"
              // as the hero pill, not Offline (register-first).
              const view = awaitingFirst
                ? { tone: 'info' as const, label: tHome('connection.waitingPill') }
                : viewForConnectorStatus(liveStatus, (live ?? charger).is_online);
              return (
                <li key={c.connector_id} className={styles.specRow}>
                  <span className={styles.specLabel}>#{c.connector_id}</span>
                  <span className={styles.specValue}>
                    {c.type} · {c.max_power_kw} kW
                  </span>
                  <StatusPill tone={view.tone} variant="soft" size="sm">
                    {view.label}
                  </StatusPill>
                </li>
              );
            })}
          </ul>
        )}
        <p className={styles.cardFooterHint}>
          {t('detail.connector.vendor')}: {charger.vendor || '—'} · {t('detail.connector.model')}: {charger.model || '—'} ·
          {' '}OCPP {charger.ocpp_version}
          {connector ? '' : ''}
        </p>
      </section>

      {/* Register-first: once connected, the setup details fold into one
          row, for a charger reset or a new router. */}
      {connectionView && !awaitingFirst && (
        <ConnectionDetailsCard
          chargerId={chargerId}
          connection={connectionView}
          variant="details"
          onChanged={refreshConnection}
        />
      )}

      {/* ── Settings form ──────────────────────────────────────── */}
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <h2 className={styles.cardTitle}>{t('detail.settingsTitle')}</h2>

        {/* Anchored + scrolled into view on set. This block sits ~270
            lines above the Save button, so on a phone a validation
            failure used to render entirely off-screen: the button
            appeared to do nothing, six times running. An error the user
            cannot see is the same as no error at all. */}
        {errorMsg && (
          <FormError announce={errorMsg.nonce}>{errorMsg.message}</FormError>
        )}

        {needsExpressOnboarding && (
          <div className={styles.gateBlock} role="alert">
            <p className={styles.gateTitle}>{t('detail.gate.title')}</p>
            <p className={styles.gateBody}>
              {t('detail.gate.body')}
            </p>
            <button
              type="button"
              className={styles.gateBtn}
              onClick={async () => {
                try {
                  // The shared connect-status read. An unreadable status
                  // goes to the chooser too, which reads it itself: only
                  // a known account resumes straight at Stripe.
                  const status = await connectQc.fetchQuery({
                    queryKey: ['connect-status'], queryFn: fetchConnectStatus, staleTime: 30_000,
                  }).catch(() => undefined);
                  const next = await startPayoutsOrChoose(status);
                  // Back from the chooser returns to this charger.
                  if (next.kind === 'chooser') { onNavigate(payoutChooserPath(`/my-chargers/${chargerId}`)); return; }
                  window.location.href = next.url;
                } catch (err) {
                  setErrorMsg(err instanceof Error ? err.message : t('detail.error.payoutStartFailed'));
                }
              }}
            >
              {t('detail.gate.cta')}
            </button>
          </div>
        )}

        {/* Item 6: open to guests with no guest price, so every paying
            guest is turned away. Says why and what fixes it: at a home a
            guest price (or earning first). At a building, where paid guest
            charging isn't available yet, turning Open to guests off is the
            fix, and the free pass list is how to let someone charge. */}
        {priceBanner && (
          <div ref={guestBannerRef} className={styles.gateBlock} role="status">
            <p className={styles.gateTitle}>{t('detail.guestPriceMissing.title')}</p>
            <p className={styles.gateBody}>
              {priceBanner === 'home_set_price'
                ? t('detail.guestPriceMissing.bodyHomeSetPrice')
                : priceBanner === 'home_enroll'
                  ? t('detail.guestPriceMissing.bodyHomeEnroll')
                  : t('detail.guestPriceMissing.bodyBuilding')}
            </p>
            <button
              type="button"
              className={styles.gateBtn}
              onClick={() => {
                if (priceBanner === 'home_enroll') {
                  onNavigate(`/my-chargers/${chargerId}/earn`);
                  return;
                }
                if (priceBanner === 'building') {
                  goToOpenToGuests();
                  return;
                }
                const input = document.getElementById('guest-rate-input');
                input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                (input as HTMLInputElement | null)?.focus({ preventScroll: true });
              }}
            >
              {priceBanner === 'home_set_price'
                ? t('detail.guestPriceMissing.ctaSetPrice')
                : priceBanner === 'home_enroll'
                  ? t('detail.guestPriceMissing.ctaEnroll')
                  : t('detail.guestPriceMissing.ctaOpenToGuests')}
            </button>
            {priceBanner === 'building' && (
              <button type="button" className={styles.gateLink} onClick={scrollToPassList}>
                {t('detail.guestPriceMissing.ctaPassList')}
              </button>
            )}
          </div>
        )}

        {/* Slice D — unenrolled home: the monetization controls stay
            hidden until the home is enrolled, and the slot carries the
            earn card instead. The disclaimer, Stripe Express and the
            enroll call all live in the walk at /my-chargers/:id/earn
            now — this card only quotes the suggested price against the
            owner's estimate and points there. The charger query has no
            staleTime, so coming back from the walk re-reads
            home_monetization.enrolled and swaps the card for the
            unlocked settings. Multifamily and enrolled homes fall
            through. */}
        {isHome && !home.enrolled && (
          <EarnPromptCard
            title={t('detail.earnCard.title')}
            subtitle={t('detail.earnPrompt.subtitle')}
            utilityRate={home.utility_rate}
            connectionFeeCents={home.connection_fee_cents}
            currency={home.currency}
            primaryLabel={t('detail.earnCard.setUp')}
            onPrimary={() => onNavigate(`/my-chargers/${chargerId}/earn`)}
          />
        )}

        {/* Open to guests — master switch */}
        {(!isHome || home.enrolled) && (
        <div className={styles.toggleRow} ref={openToGuestsRowRef}>
          <div className={styles.toggleInfo}>
            <p className={styles.toggleLabel}>{t('detail.openToGuests.label')}</p>
            <p className={styles.toggleHint}>
              {isHome
                ? t('detail.openToGuests.hintHome')
                : isBuildingResident
                  ? (openLocked
                    ? t('detail.openToGuests.hintBuildingResidentClosed')
                    : t('detail.openToGuests.hintBuildingResidentOpen'))
                  : t('detail.openToGuests.hintBuilding')}
            </p>
          </div>
          {/* A building resident's closed charger stays closed (the Api
              refuses it, 409); one already open can still be closed. */}
          <SwitchInput checked={isOpen} onChange={setIsOpen} disabled={saving || openLocked} />
        </div>
        )}

        {/* Slice D — home guest price: ONE $/kWh field. The home's own
            rate is 0 by design (owner and whitelist charge free), so
            the multifamily markup UI below never renders here; the
            guest price IS the rate, stored on the Home Guest tariff. */}
        {isHome && home.enrolled && (
          <div className={formStyles.field}>
            <label htmlFor="guest-rate-input" className={formStyles.label}>
              {t('detail.guestPrice.label')}
              {!isOpen && (
                <span className={styles.fieldNote}>{t('detail.enableToApply')}</span>
              )}
            </label>
            <div className={styles.pricingInputGroup}>
              <span className={styles.pricingPrefix} aria-hidden>{m.symbol}</span>
              <input
                id="guest-rate-input"
                className={homeInputClass}
                type="number"
                min={0.01}
                max={2}
                step={0.01}
                placeholder="0.25"
                value={guestRateStr}
                onChange={(e) => setGuestRateStr(e.target.value)}
                disabled={saving}
                aria-label={t('detail.guestPrice.aria')}
              />
              <span className={styles.pricingSuffix} aria-hidden>/kWh</span>
            </div>
            <p className={formStyles.hint}>
              {t('detail.guestPrice.hint')}
            </p>
            {/* A home priced before item 3: what guests pay today, and
                what the next save does. Codes (USD, CAD), so the two
                currencies read apart in every language. */}
            {currencyMove && storedPriceCurrency && (
              <p className={formStyles.hint} role="note">
                {t('detail.guestPrice.currencyChange', { from: storedPriceCurrency, to: m.currency })}
              </p>
            )}

            {/* Utility-rate context + TRUE margin line. The host
                receives 85% of the guest price (destination charge
                minus EVI's 15% application fee — verified against
                StopTransaction PATH A), so margin = 0.85 x guest price
                minus the owner's electricity estimate. Computed LIVE
                from the input as typed, so the number answers "is
                this price worth it" before Save. TOU/tiered rates
                show the honest range, never a flattering midpoint. */}
            <div className={styles.utilityRateRow}>
              <span className={styles.utilityRateLabel}>{t('detail.utilityRate.label')}</span>
              <span className={styles.utilityRateValue}>
                {utilityDisplay ?? t('detail.utilityRate.notSet')}
              </span>
              <button
                type="button"
                className={styles.utilityRateLink}
                onClick={() => onNavigate('/my-home/settings')}
              >
                {t('detail.utilityRate.setLink')}
              </button>
            </div>
            <p className={formStyles.hint}>{t('detail.utilityRate.note')}</p>
            {/* EVI's connection fee (read-only) + the idle fee.

                The connection fee is shown, never edited: EVI sets it
                per charger and keeps it whole (Tier 2), guests pay it
                on top of the price, and it never comes out of the
                host's 85%. The idle fee is the host's; its keep line
                quotes the NET, because it is not exempt from EVI's 15%
                — it settles through the same destination charge as the
                energy, so a gross number would overstate the host's
                take by exactly the carve-out. */}
            <div className={styles.feeGrid}>
              <div className={formStyles.field}>
                <span className={formStyles.label}>
                  {t('detail.connectionFee.label')}
                </span>
                <p className={styles.feeReadOnly}>
                  {connectionFeeCents != null && connectionFeeCents > 0
                    ? t('detail.connectionFee.value', { fee: m.fee(connectionFeeCents) })
                    : t('detail.connectionFee.none')}
                </p>
                {connectionFeeCents != null && connectionFeeCents > 0 && (
                  <p className={formStyles.hint}>{t('detail.connectionFee.hint')}</p>
                )}
              </div>

              <div className={formStyles.field}>
                <label htmlFor="idle-fee-input" className={formStyles.label}>
                  {t('detail.idleFee.label')}
                </label>
                <div className={styles.pricingInputGroup}>
                  <span className={styles.pricingPrefix} aria-hidden>{m.symbol}</span>
                  <input
                    id="idle-fee-input"
                    className={homeInputClass}
                    type="number"
                    min={0}
                    max={HOME_IDLE_RATE_MAX_CENTS / 100}
                    step={0.01}
                    placeholder="0.00"
                    value={idleFeeStr}
                    onChange={(e) => setIdleFeeStr(e.target.value)}
                    disabled={saving}
                  />
                  <span className={styles.pricingSuffix} aria-hidden>/min</span>
                </div>
                <p className={formStyles.hint}>
                  {renderInline(idleText(i18n.language, 'rateHint', { max: idleMoney(HOME_IDLE_RATE_MAX_CENTS, m.currency, i18n.language) }))}
                </p>
                {idleKeepCents != null && (
                  <p className={formStyles.hint}>
                    {renderInline(idleText(i18n.language, 'keep', { amount: idleMoney(idleKeepCents, m.currency, i18n.language) }))}
                  </p>
                )}
                {idleMaxCents != null && (
                  <p className={`${formStyles.hint} ${styles.idleMax}`}>
                    {renderInline(idleHostSummary(typedIdleCents!, parseDollarsToCents(idleLimitStr), m.currency, i18n.language))}
                  </p>
                )}
              </div>

              <div className={formStyles.field}>
                <label htmlFor="idle-limit-input" className={formStyles.label}>{t('detail.idleFee.limitLabel')}</label>
                <div className={styles.pricingInputGroup}>
                  <span className={styles.pricingPrefix} aria-hidden>{m.currency === 'CAD' ? 'CA$' : 'US$'}</span>
                  <input id="idle-limit-input" aria-label={`${t('detail.idleFee.limitLabel')} (${m.currency})`} className={`${styles.pricingInput} ${styles.pricingInputWide}`} type="number" min={0} step={0.01}
                    value={idleLimitStr} onChange={e => setIdleLimitStr(e.target.value)} disabled={saving}
                    placeholder={t('detail.idleFee.limitPlaceholder')} />
                </div>
                <p className={formStyles.hint}>{renderInline(idleText(i18n.language, 'limitHint'))}</p>
              </div>
              <div className={formStyles.field}>
                <label htmlFor="idle-grace-input" className={formStyles.label}>
                  {t('detail.idleGrace.label')}
                </label>
                <div className={styles.pricingInputGroup}>
                  <input
                    id="idle-grace-input"
                    className={styles.pricingInput}
                    type="number"
                    inputMode="numeric"
                    min={IDLE_GRACE_MIN_MINUTES}
                    max={IDLE_GRACE_MAX_MINUTES}
                    step={1}
                    value={idleGraceStr}
                    onChange={(e) => setIdleGraceStr(e.target.value)}
                    disabled={saving || !hasTypedIdleFee}
                  />
                  <span className={styles.pricingSuffix} aria-hidden>{t('detail.idleGrace.suffix')}</span>
                </div>
                <p className={formStyles.hint}>
                  {renderInline(idleText(i18n.language, 'graceHint'))}
                </p>
              </div>
            </div>

            {marginLine && (
              <p className={marginLine.loss ? styles.marginNoteWarn : styles.marginNote}>
                {marginLine.text}
              </p>
            )}
          </div>
        )}

        {/* Item 6: a building resident's charger has no guest price to
            set. The retired markup editor's place says so and points at
            the free pass list, the way to let someone charge here. */}
        {isBuildingResident && (
          <div className={formStyles.field}>
            <span className={formStyles.label}>{t('detail.buildingGuests.label')}</span>
            <p className={formStyles.hint}>{t('detail.buildingGuests.body')}</p>
            <button
              type="button"
              className={styles.utilityRateLink}
              onClick={scrollToPassList}
            >
              {t('detail.buildingGuests.cta')}
            </button>
          </div>
        )}

        {!isHome && !isBuildingResident && (
          <>
        {/* Guest pricing — dual $ / % input + math explainer (B.1).
            The $ side is presentation only; the wire format is bps
            via guest_markup_bps. Editing one side recomputes the
            other through the building's resolved rate.
            Four variant paths driven by location.resolved_rate.source:
              - 'flat'                  → happy path, single ref rate
              - 'inline_tou' / 'tariff' → TOU range, $ math uses average
              - rate_cents===0          → warn banner, inputs still editable
              - 'none'                  → block banner, inputs disabled */}
        <div className={formStyles.field}>
          <label htmlFor="markup-input" className={formStyles.label}>
            {t('detail.markup.label')}
            {!isOpen && (
              <span className={styles.fieldNote}>{t('detail.enableToApply')}</span>
            )}
          </label>

          {renderPricingBanner(t, resolved)}

          <div className={styles.pricingRow}>
            <div className={styles.pricingInputBlock}>
              <span className={styles.pricingInputLabel}>
                {resolved?.rate_type === 'per_hour' ? t('detail.markup.perHour') : t('detail.markup.perKwh')}
              </span>
              <div className={styles.pricingInputGroup}>
                <span className={styles.pricingPrefix} aria-hidden>$</span>
                <input
                  id="markup-dollar-input"
                  className={styles.pricingInput}
                  type="number"
                  min={0}
                  step={0.0001}
                  placeholder="0.0000"
                  value={markupDollarStr}
                  onChange={(e) => handleDollarChange(e.target.value)}
                  disabled={
                    !isOpen
                    || saving
                    || resolved?.source === 'none'
                    || buildingRefRateCents == null
                    || buildingRefRateCents === 0
                  }
                  aria-label={
                    resolved?.rate_type === 'per_hour'
                      ? t('detail.markup.ariaDollarHour')
                      : t('detail.markup.ariaDollarKwh')
                  }
                />
                <span className={styles.pricingSuffix} aria-hidden>
                  {resolved?.rate_type === 'per_hour' ? '/hour' : '/kWh'}
                </span>
              </div>
            </div>

            <div className={styles.pricingInputBlock}>
              <span className={styles.pricingInputLabel}>{t('detail.markup.percent')}</span>
              <div className={styles.pricingInputGroup}>
                <input
                  id="markup-input"
                  className={`${styles.pricingInput} ${styles.pricingInputPct}`}
                  type="number"
                  min={0}
                  max={500}
                  step={0.1}
                  placeholder={t('detail.markup.placeholder')}
                  value={markupPctStr}
                  onChange={(e) => handlePctChange(e.target.value)}
                  disabled={!isOpen || saving || resolved?.source === 'none'}
                  aria-label={t('detail.markup.ariaPercent')}
                />
                <span className={styles.pricingSuffix} aria-hidden>%</span>
              </div>
            </div>
          </div>

          {resolved && resolved.source !== 'none'
            && renderPricingMath(
              t,
              resolved,
              buildingRefRateCents,
              markupBpsForSave,
              detectMixedTouUnit(loc?.electricity_tou_windows),
            )}

          {resolved && resolved.source !== 'none' && (
            <p className={styles.pricingDisclosure}>
              {t('detail.markup.disclosure')}
            </p>
          )}

          <p className={formStyles.hint}>
            {t('detail.markup.hint')}
          </p>
        </div>
          </>
        )}

        {/* Public network */}
        <div className={styles.toggleRow}>
          <div className={styles.toggleInfo}>
            <p className={styles.toggleLabel}>{t('detail.publicNetwork.label')}</p>
            <p className={styles.toggleHint}>
              {t('detail.publicNetwork.hint')}
            </p>
          </div>
          <SwitchInput
            checked={publicNetwork}
            onChange={setPublicNetwork}
            disabled={saving}
          />
        </div>

        {/* Share — delivers the publicNetwork hint's promise: the
            direct link + QR that keep this charger reachable whether
            or not it is listed on the map. */}
        <ShareChargerSection
          identity={charger.identity}
          locationId={charger.location_id}
          buildingResident={isBuildingResident}
        />

        {/* Free pass list */}
        <div className={styles.subsection} ref={passListSectionRef}>
          <p className={styles.subsectionTitle}>{t('detail.passList.title')}</p>
          <p className={styles.subsectionHint}>
            {isHome
              ? t('detail.passList.hintHome')
              : isBuildingResident
                ? t('detail.passList.hintBuildingResident')
                : t('detail.passList.hintBuilding')}
            {/* Same list as Home Settings' Free pass list (identical
                Charger.guest_pass_list field) — home branch only,
                since multifamily residents have no Home Settings. */}
            {isHome && (
              <>
                {' '}
                <button
                  type="button"
                  className={styles.utilityRateLink}
                  onClick={() => onNavigate('/my-home/settings')}
                >
                  {t('detail.passList.alsoIn')}
                </button>
              </>
            )}
          </p>
          <UserTypeahead
            selectedUserIds={passList}
            onChange={setPassList}
            maxSelections={100}
            disabled={saving}
            chargerId={chargerId}
            ariaLabel={t('detail.passList.aria')}
            crossTenantLabel={isHome ? t('detail.passList.crossTenant') : undefined}
            knownMembers={charger.guest_pass_list_members}
          />
        </div>

        {/* Blocked drivers — deny beats everything including the
            pass list; the gates make a denied guest indistinguishable
            from the charger being closed. */}
        <div className={styles.subsection}>
          <p className={styles.subsectionTitle}>{t('detail.denyList.title')}</p>
          <p className={styles.subsectionHint}>{t('detail.denyList.hint')}</p>
          <UserTypeahead
            selectedUserIds={denyList}
            onChange={setDenyList}
            maxSelections={200}
            disabled={saving}
            ariaLabel={t('detail.denyList.aria')}
            knownMembers={charger.guest_deny_list_members}
          />
        </div>

        {/* Guest time windows — inline TimeWindowRows pattern. The
            third caller; Phase 7 of this ticket extracts a shared
            component when the electricity-tariff editor lands.
            Item 6: hidden at a building resident's closed charger, which
            can't be opened to guests, so its hours govern nothing (the
            stored windows ride the save unchanged). One already open
            keeps the editor and says the hours don't apply. */}
        {!openLocked && (
        <div className={styles.subsection}>
          <p className={styles.subsectionTitle}>{t('detail.windows.title')}</p>
          <p className={styles.subsectionHint}>
            {isBuildingResident ? t('detail.windows.hintBuildingResident') : t('detail.windows.hint')}
          </p>
          {/* The screen must never claim a clock the gate isn't using.
              With a zone set we name it; without one we say plainly
              that hours run on UTC, and offer the one-tap fix where
              the owner is entitled to make it (their own home). */}
          <WindowTimeZoneNotice
            chargerId={chargerId}
            timezone={charger.window_timezone ?? null}
            canSet={charger.window_timezone_can_set === true}
            disabled={saving}
            onSaved={(tz) => onSaved({ ...charger, window_timezone: tz })}
          />

          <TimeWindowRows<GuestTimeWindowState>
            windows={windows}
            onChange={setWindows}
            getDays={(w) => w.days_of_week}
            setDays={(w, days) => ({ ...w, days_of_week: days })}
            getStartHour={(w) => w.start_hour}
            setStartHour={(w, h) => ({ ...w, start_hour: h })}
            getEndHour={(w) => w.end_hour}
            setEndHour={(w, h) => ({ ...w, end_hour: h })}
            // 9-17 default mirrors the inline pattern this replaces —
            // standard business hours, empty days so the resident
            // picks at least one to make the row meaningful.
            createEmpty={() => ({
              days_of_week: [], start_hour: 9, end_hour: 17, _key: nextWindowKey(),
            })}
            getKey={(w) => w._key}
            emptyStateText={t('detail.windows.empty')}
            disabled={saving}
          />
        </div>
        )}

        <div className={styles.formActions}>
          <button
            type="submit"
            className={formStyles.btnPrimary}
            disabled={saving}
          >
            {saving
              ? <><Loader2 size={14} className={styles.spin} /> {t('detail.save.saving')}</>
              : <><Save size={14} /> {t('detail.save.submit')}</>}
          </button>
        </div>
      </form>

      {/* Owner-side disclaimer modal — pops on first pass-list add.
          The modal calls handleAckDisclaimer which acks server-side
          and replays the queued PATCH; on cancel we drop the pending
          body and let the resident decide whether to try again. */}
      {pendingPatch && (
        <DisclaimerModal
          title={t('detail.disclaimer.title')}
          body={
            // Twin disclaimer copy — owner-side. Recipient-side
            // counterpart lives at MyAccessPage.tsx:190. The two
            // surfaces frame the same legal/expectation paragraph
            // for the two sides of the relationship; if either
            // edits, the other should match.
            <>
              EVI facilitates this connection. The agreement (times,
              pricing, expectations) is between you and the other
              driver. We process payments and surface contact info.
              We don&rsquo;t enforce or arbitrate.
            </>
          }
          onAck={handleAckDisclaimer}
          onCancel={() => {
            setPendingPatch(null);
            setErrorMsg(t('detail.disclaimer.cancelMsg'));
          }}
        />
      )}
    </>
  );
}

/**
 * Share this charger — direct link + QR.
 *
 * URL composition matches admin's qr-generator.ts exactly (the
 * canonical v1 sticker format parsed by lib/qr.ts):
 *   <origin>/charging-station/<locationId>?charger=<identity>
 * The PWA's own origin is used instead of admin's VITE_PUBLIC_PWA_HOST
 * because this page IS the PWA — stage builds automatically share
 * stage links, production shares production.
 *
 * The QR renders client-side via the same zero-dep `qrcode` lib admin
 * uses, dynamically imported so it stays off the initial bundle. The
 * encoded URL is non-sensitive — identical to what a field sticker
 * carries. Who can actually charge from the link is decided entirely
 * server-side (pass list / open-to-guests gates); the link works even
 * with the public map listing off.
 */
function ShareChargerSection({ identity, locationId, buildingResident }: {
  identity: string;
  locationId: string;
  /** Item 6: a building resident's charger has no paying guests, so the
   *  hint names only the free pass list. */
  buildingResident: boolean;
}) {
  const { t } = useTranslation('myChargers');
  const [copied, setCopied] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  const url = `${window.location.origin}/charging-station/${encodeURIComponent(locationId)}?charger=${encodeURIComponent(identity)}`;

  useEffect(() => {
    let cancelled = false;
    void import('qrcode')
      .then(({ default: QRCode }) => QRCode.toDataURL(url, { width: 480, margin: 1 }))
      .then((dataUrl) => { if (!cancelled) setQrDataUrl(dataUrl); })
      .catch(() => { /* QR is progressive enhancement — the link row stands alone. */ });
    return () => { cancelled = true; };
  }, [url]);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast(t('detail.share.copyError'), { tone: 'warn' });
    }
  };

  const share = async (): Promise<void> => {
    try {
      await navigator.share({ title: t('detail.share.shareTitle'), url });
    } catch {
      // Dismissed the share sheet — nothing to do.
    }
  };

  return (
    <div className={styles.subsection}>
      <p className={styles.subsectionTitle}>
        <Link2 size={14} aria-hidden /> {t('detail.share.title')}
      </p>
      <p className={styles.subsectionHint}>
        {buildingResident ? t('detail.share.hintBuildingResident') : t('detail.share.hint')}
      </p>
      <div className={styles.shareRow}>
        <span className={styles.shareUrl}>{url}</span>
        <button type="button" className={styles.shareBtn} onClick={() => { void copy(); }}>
          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? t('detail.share.copied') : t('detail.share.copy')}
        </button>
        {typeof navigator.share === 'function' && (
          <button type="button" className={styles.shareBtn} onClick={() => { void share(); }}>
            <Share2 size={13} /> {t('detail.share.share')}
          </button>
        )}
      </div>
      {qrDataUrl && (
        <div className={styles.shareQrWrap}>
          {/* White backing keeps the QR scannable in dark themes. */}
          <img className={styles.shareQr} src={qrDataUrl} alt={t('detail.share.qrAlt')} />
        </div>
      )}
    </div>
  );
}

/**
 * Timezone honesty notice for the guest-window editor.
 *
 * The bug this whole change fixes was a silent one: owners typed
 * hours into an unlabeled picker and the server enforced them in UTC.
 * So this component's rule is that the screen states the clock the
 * GATE is actually using — never a friendlier one.
 *
 *  - zone set        -> name it ("Hours are in this charger's local
 *                       time — Toronto")
 *  - null + home     -> warn that hours run on UTC, offer one tap to
 *                       adopt the device's zone (the owner is the
 *                       only stakeholder in their own home's clock)
 *  - null + building -> warn, and point at the building admin: one
 *                       resident must not move a shared site's clock
 */
function WindowTimeZoneNotice({
  chargerId, timezone, canSet, disabled, onSaved,
}: {
  chargerId: string;
  timezone: string | null;
  canSet: boolean;
  disabled: boolean;
  onSaved: (tz: string) => void;
}) {
  const { t } = useTranslation('myChargers');
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (timezone) {
    return (
      <p className={styles.tzOk}>
        {t('detail.windows.tzSet', { zone: zoneCityLabel(timezone) })}
      </p>
    );
  }

  const adopt = async (): Promise<void> => {
    setBusy(true);
    try {
      const res = await setMyChargerTimezone(chargerId, deviceZone);
      onSaved(res.timezone);
      // The zone is the home Location's: Home Settings' Time zone row
      // reads it from ['my-home'].
      void qc.invalidateQueries({ queryKey: ['my-home'] });
      toast(t('detail.windows.tzSavedToast', { zone: zoneCityLabel(res.timezone) }), { tone: 'accent' });
    } catch (err) {
      const { message } = await readApiErrorEnvelope(err);
      toast(message ?? t('detail.windows.tzSaveError'), { tone: 'warn' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.tzWarn}>
      <p className={styles.tzWarnTitle}>{t('detail.windows.tzMissingTitle')}</p>
      <p className={styles.tzWarnBody}>
        {canSet ? t('detail.windows.tzMissingHome') : t('detail.windows.tzMissingBuilding')}
      </p>
      {canSet && (
        <button
          type="button"
          className={styles.tzAdoptBtn}
          disabled={disabled || busy}
          onClick={() => { void adopt(); }}
        >
          {busy
            ? <><Loader2 size={13} className={styles.spin} /> {t('detail.windows.tzSaving')}</>
            : t('detail.windows.tzUseMine', { zone: zoneCityLabel(deviceZone) })}
        </button>
      )}
    </div>
  );
}

/** "America/Toronto" -> "Toronto". Never invents an abbreviation. */
function zoneCityLabel(zone: string): string {
  const city = zone.split('/').pop();
  return city ? city.replace(/_/g, ' ') : zone;
}

// ── B.1 + B.1.1 pricing helpers ───────────────────────────────────
// formatRateValue — 4 decimal places because electricity rates are
// often sub-cent ($0.0315). Intl.NumberFormat with `currency: USD`
// would clamp at 2 decimals, hence the manual format. Used for
// both per_kwh and per_hour values; the unit suffix is appended by
// the caller via `rateUnitSuffix` so the number-formatting logic
// stays unit-agnostic.
function formatRateValue(cents: number): string {
  return `$${(cents / 100).toFixed(4)}`;
}

/** "/kWh" or "/hour" depending on rate_type. Centralized so every
 *  pricing-row template references the same source. */
function rateUnitSuffix(rateType: 'per_kwh' | 'per_hour'): string {
  return rateType === 'per_hour' ? '/hour' : '/kWh';
}

/** Detect whether an inline-TOU window list mixes units. Used by
 *  the resident-side pricing UI (B.1.1) to surface a "varies by
 *  time and unit" note instead of a single-unit range that would
 *  read deceptively when the schedule itself spans both units. */
function detectMixedTouUnit(
  windows: Array<{ rate_type?: 'per_kwh' | 'per_hour' }> | null | undefined,
): boolean {
  if (!windows || windows.length === 0) return false;
  let sawKwh = false;
  let sawHour = false;
  for (const w of windows) {
    const t = w.rate_type ?? 'per_kwh';
    if (t === 'per_hour') sawHour = true;
    else sawKwh = true;
    if (sawKwh && sawHour) return true;
  }
  return false;
}

/** Banner above the dual input — surfaces non-flat building-rate
 *  states. Returns null on the happy path (flat with a rate > 0).
 *  rateType drives the zero-rate banner's unit string. */
function renderPricingBanner(
  t: TFunction<'myChargers'>,
  resolved: {
    rate_cents: number | null;
    source: 'tariff' | 'inline_tou' | 'flat' | 'none';
    rate_type: 'per_kwh' | 'per_hour';
    tou_min_cents: number | null;
    tou_max_cents: number | null;
  } | null,
): ReactNode {
  if (!resolved) return null;
  if (resolved.source === 'none') {
    return (
      <div className={`${styles.pricingBanner} ${styles.pricingBannerBlock}`}>
        {t('detail.banner.notConfigured')}
      </div>
    );
  }
  if (resolved.source === 'flat' && resolved.rate_cents === 0) {
    const unit = rateUnitSuffix(resolved.rate_type);
    return (
      <div className={`${styles.pricingBanner} ${styles.pricingBannerWarn}`}>
        {t('detail.banner.zeroRate', { unit })}
      </div>
    );
  }
  return null;
}

/** Three-line math block below the dual input. The values that
 *  change as the resident edits the input rebind off the live
 *  `markupBps` rather than the stored charger snapshot, so the
 *  numbers move in real time before save.
 *
 *  B.1.1 — rate_type dispatch. Single-unit schedules render with
 *  the matching suffix ("/kWh" or "/hour"). Mixed-unit inline-TOU
 *  schedules render with a "varies by time and unit" note since
 *  the tou_min/max range mixes magnitudes across units. */
function renderPricingMath(
  t: TFunction<'myChargers'>,
  resolved: {
    rate_cents: number | null;
    source: 'tariff' | 'inline_tou' | 'flat' | 'none';
    rate_type: 'per_kwh' | 'per_hour';
    tou_min_cents: number | null;
    tou_max_cents: number | null;
  },
  buildingRefRateCents: number | null,
  markupBpsLive: number | null,
  isMixedTouUnit: boolean,
): ReactNode {
  const isTou = resolved.source === 'inline_tou' || resolved.source === 'tariff';
  const bps = markupBpsLive ?? 0;
  const multiplier = 1 + bps / 10_000;
  const unit = rateUnitSuffix(resolved.rate_type);

  // Mixed-unit inline TOU — surface a single advisory row instead
  // of a range that would mix incompatible magnitudes across units
  // (e.g. 18¢/kWh and 100¢/hour rendered as "18 – 100" reads as
  // nonsense). Pricing math is still computed against the
  // buildingRefRateCents average, but the display tells the user
  // the value is approximate.
  if (isTou && isMixedTouUnit) {
    return (
      <div className={styles.pricingMath}>
        <div className={styles.pricingMathRow}>
          <span className={styles.pricingMathLabel}>{t('detail.math.buildingRate')}</span>
          <span className={styles.pricingMathValue}>{t('detail.math.variesTimeUnit')}</span>
        </div>
        <div className={styles.pricingMathRow}>
          <span className={styles.pricingMathLabel}>{t('detail.math.yourMarkup')}</span>
          <span className={styles.pricingMathValue}>{t('detail.math.acrossWindows', { pct: (bps / 100).toFixed(1) })}</span>
        </div>
      </div>
    );
  }

  if (isTou
      && resolved.tou_min_cents != null
      && resolved.tou_max_cents != null) {
    const guestMin = resolved.tou_min_cents * multiplier;
    const guestMax = resolved.tou_max_cents * multiplier;
    const earnMin = resolved.tou_min_cents * (bps / 10_000);
    const earnMax = resolved.tou_max_cents * (bps / 10_000);
    return (
      <div className={styles.pricingMath}>
        <div className={styles.pricingMathRow}>
          <span className={styles.pricingMathLabel}>{t('detail.math.buildingRateTou')}</span>
          <span className={styles.pricingMathValue}>
            {formatRateValue(resolved.tou_min_cents)} – {formatRateValue(resolved.tou_max_cents)}{unit}
          </span>
        </div>
        <div className={styles.pricingMathRow}>
          <span className={styles.pricingMathLabel}>{t('detail.math.guestsPay')}</span>
          <span className={styles.pricingMathValue}>
            {formatRateValue(guestMin)} – {formatRateValue(guestMax)}{unit}
          </span>
        </div>
        <div className={styles.pricingMathRow}>
          <span className={styles.pricingMathLabel}>{t('detail.math.youEarn')}</span>
          <span className={styles.pricingMathValueEarn}>
            {formatRateValue(earnMin)} – {formatRateValue(earnMax)}{unit}
          </span>
        </div>
      </div>
    );
  }

  // Flat path — single ref rate.
  if (buildingRefRateCents == null) return null;
  const guest = buildingRefRateCents * multiplier;
  const earn = buildingRefRateCents * (bps / 10_000);
  return (
    <div className={styles.pricingMath}>
      <div className={styles.pricingMathRow}>
        <span className={styles.pricingMathLabel}>{t('detail.math.buildingRate')}</span>
        <span className={styles.pricingMathValue}>{formatRateValue(buildingRefRateCents)}{unit}</span>
      </div>
      <div className={styles.pricingMathRow}>
        <span className={styles.pricingMathLabel}>{t('detail.math.guestsPay')}</span>
        <span className={styles.pricingMathValue}>{formatRateValue(guest)}{unit}</span>
      </div>
      <div className={styles.pricingMathRow}>
        <span className={styles.pricingMathLabel}>{t('detail.math.youEarn')}</span>
        <span className={styles.pricingMathValueEarn}>{formatRateValue(earn)}{unit}</span>
      </div>
    </div>
  );
}

/** Minimal switch primitive used inline by the toggle rows on this
 *  page. The PWA primitives barrel doesn't export a switch yet —
 *  rather than redirecting that change through this commit, we
 *  inline a small pill switch matching the SettingsPage admin one. */
function SwitchInput({
  checked, onChange, disabled,
}: { checked: boolean; onChange: (next: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={checked ? `${styles.switch} ${styles.switchOn}` : styles.switch}
      onClick={() => onChange(!checked)}
    >
      <span className={styles.switchKnob} />
    </button>
  );
}

// ── Owner remote start (charger page polish pass) ─────────────────
// Mirror of the My Home StartChargeSection: same startDriverCharger
// helper, same active-session handoff. The disable condition rides
// the canonical viewForConnectorStatus selectable signal (Available
// and Plugged in start; every other state explains itself in the
// note) instead of forking a second status interpretation.
function OwnerStartSection({
  charger, statusView, isOnline, onNavigate,
}: {
  charger: MyChargerDetail;
  statusView: ConnectorStatusView;
  isOnline: boolean;
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation('myChargers');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<DriverStartError | null>(null);
  // Reconnect rescue state. idle -> nudging -> (watching | recovery).
  // A homeowner whose charger drops must have a live recovery path, never
  // a dead greyed button.
  const [recovery, setRecovery] = useState<'idle' | 'nudging' | 'watching' | 'recovery'>('idle');
  const [nudgeError, setNudgeError] = useState<string | null>(null);

  // The 10s my-chargers poll drives isOnline. When the charger comes back
  // during a plain nudge/watch, celebrate and reset — the Start button
  // returns on its own now that isOnline is true. The guided-recovery
  // state ('recovery') owns its OWN reconnect celebration inside
  // ChargerRecoveryFlow, so it is deliberately excluded here.
  useEffect(() => {
    if (isOnline && (recovery === 'nudging' || recovery === 'watching')) {
      toast(t('detail.owner.backOnline'), { tone: 'accent' });
      setRecovery('idle');
      setNudgeError(null);
    }
  }, [isOnline, recovery]);

  const startQc = useQueryClient();
  const start = async (): Promise<void> => {
    setStartError(null);
    setStarting(true);
    try {
      const started = await startDriverCharger(charger.identity, {
        connector_id: charger.connectors[0]?.connector_id ?? 1,
      });
      // The pending shell exists server-side now; refetch the shared
      // live-session cache so the home (HomeRoute) and the mini bar see
      // it at once rather than one 15s poll later.
      startQc.invalidateQueries({ queryKey: ACTIVE_SESSION_KEY });
      // The Api started the other plug, where the car is plugged in
      // (Warren, 29 Oct 2026): say which, as PreSessionPage does.
      const plugNotice = startedPlugNotice(started);
      if (plugNotice) {
        toast(t('toast.startedOtherPlug', { ns: 'preSession', plug: plugNotice.plug }), { tone: 'accent', durationMs: 6000 });
      } else {
        toast(t('detail.owner.sessionStarted'), { tone: 'accent' });
      }
      onNavigate(`/charging/active?charger=${encodeURIComponent(charger.identity)}`);
    } catch (err) {
      // startDriverCharger throws a structured DriverStartError, not a plain Error.
      setStartError(err as DriverStartError);
    } finally {
      setStarting(false);
    }
  };

  const nudge = async (): Promise<void> => {
    setNudgeError(null);
    setRecovery('nudging');
    try {
      const res = await nudgeCharger(charger.identity);
      // connected -> the server forced a re-announce; watch the poll flip
      // is_online back on. not connected -> nothing to nudge, so go to
      // guided recovery immediately rather than spin on a dead line.
      setRecovery(res.connected ? 'watching' : 'recovery');
    } catch (err) {
      setNudgeError(err instanceof Error ? err.message : t('detail.owner.nudgeFailed'));
      setRecovery('recovery');
    }
  };

  // ── Guided recovery: EVII walks the owner back online ───────────────
  // Rendered independently of isOnline so the flow can play its own
  // reconnect celebration; it dismisses itself via onDone once done.
  if (recovery === 'recovery') {
    return (
      <div className={styles.startSection}>
        <ChargerRecoveryFlow
          identity={charger.identity}
          isOnline={isOnline}
          onDone={() => setRecovery('idle')}
        />
      </div>
    );
  }

  // ── Offline: reconnect rescue replaces the greyed Start button ──────
  if (!isOnline) {
    return (
      <div className={styles.startSection}>
        {recovery === 'idle' && (
          <>
            <button type="button" className={styles.nudgeCta} onClick={nudge}>
              <RefreshCw size={16} /> {t('detail.owner.nudge')}
            </button>
            <p className={styles.startNote}>
              {t('detail.owner.offlineNote')}
            </p>
            {/* Proactive offer: the moment an owner lands on the offline
                page, EVII is already offering to walk them through it. */}
            <button
              type="button"
              className={styles.recoveryLink}
              onClick={() => setRecovery('recovery')}
            >
              {t('detail.owner.recoveryLink')}
            </button>
          </>
        )}
        {(recovery === 'nudging' || recovery === 'watching') && (
          <div className={styles.watchingRow}>
            <Loader2 size={16} className={styles.spin} />
            <span>
              {recovery === 'nudging'
                ? t('detail.owner.nudging')
                : t('detail.owner.watching')}
            </span>
          </div>
        )}
        {nudgeError && <p className={styles.startNote}>{nudgeError}</p>}
      </div>
    );
  }

  // ── Online: the normal remote-start button ──────────────────────────
  const busyLabels = new Set(['Charging', 'Paused', 'Finishing']);
  const note = statusView.selectable
    ? null
    : busyLabels.has(statusView.label)
      ? t('detail.owner.busyNote')
      : statusView.label === 'Reserved'
        ? t('detail.owner.reservedNote')
        : t('detail.owner.unavailableNote');

  return (
    <div className={styles.startSection}>
      <button
        type="button"
        className={styles.startCta}
        disabled={!statusView.selectable || starting}
        onClick={start}
      >
        {starting ? <Loader2 size={16} className={styles.spin} /> : <Zap size={16} />}
        {starting ? t('detail.owner.starting') : t('detail.owner.startCharging')}
      </button>
      {note && <p className={styles.startNote}>{note}</p>}
      {startError && (
        <div className={styles.errorBox}>
          {startError.error}
          {startError.hint ? ` ${startError.hint}` : ''}
        </div>
      )}
    </div>
  );
}
